/**
 * Person Hooks (henkilörekisteri — epic #763)
 *
 * TanStack Query hooks for the person registry (separate from customers).
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import { fromOffsetResponse, usePagedQuery } from './usePagedQuery';

export interface Person {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
  invoiceEmail: string | null;
  whatsappNumber: string | null;
  preferredLanguage: string;
  streetAddress: string | null;
  postalCode: string | null;
  city: string | null;
  notes: string | null;
  laborRateId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PersonCompanyLink {
  id: string;
  companyId: string;
  companyName: string;
  businessId: string | null;
  role: string | null;
  isPrimary: boolean;
  validFrom: string;
  validTo: string | null;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: { nextCursor: string | null; prevCursor: string | null; hasMore: boolean };
}

export interface PersonFilters {
  [key: string]: string | number | undefined;
  q?: string;
  sortBy?: 'name' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}

export interface CreatePersonData {
  firstName: string;
  lastName: string;
  phone?: string | null;
  email?: string | null;
  invoiceEmail?: string | null;
  whatsappNumber?: string | null;
  preferredLanguage?: string;
  streetAddress?: string | null;
  postalCode?: string | null;
  city?: string | null;
  notes?: string | null;
  laborRateId?: string | null;
}

export type UpdatePersonData = Partial<CreatePersonData>;

export function usePersons(filters: PersonFilters = {}, options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    queryKey: queryKeys.persons.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<Person>>('/persons', {
        params: { ...filters, cursor: pageParam },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Page-number list for PersonsPage (#2737).
 * `usePersons` stays for pickers that still read the cursor list.
 */
export function usePagedPersons(filters: { q?: string } = {}) {
  return usePagedQuery({
    queryKey: queryKeys.persons.lists(),
    filters,
    defaultSort: { sortBy: 'name', sortDir: 'asc' },
    staleTime: 30 * 1000,
    fetchPage: async ({ filters: pageFilters, limit, offset, sortBy, sortDir }) => {
      const params: Record<string, string | number | undefined> = { limit, offset };
      if (pageFilters.q) params['q'] = pageFilters.q;
      if (sortBy !== undefined) params['sortBy'] = sortBy;
      if (sortDir !== undefined) params['sortDir'] = sortDir;
      const response = await api.get<{ data: Person[]; pagination: { total: number } }>('/persons', {
        params,
      });
      return fromOffsetResponse(response);
    },
  });
}

export function usePerson(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.persons.detail(id ?? ''),
    queryFn: () => api.get<Person>(`/persons/${id}`),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function usePersonCompanies(personId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.persons.companies(personId ?? ''),
    queryFn: () => api.get<{ data: PersonCompanyLink[] }>(`/persons/${personId}/companies`),
    enabled: !!personId,
    staleTime: 60 * 1000,
  });
}

export function useCreatePerson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePersonData) => api.post<Person>('/persons', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.persons.lists() });
    },
  });
}

export function useUpdatePerson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePersonData }) =>
      api.patch<Person>(`/persons/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.persons.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.persons.lists() });
    },
  });
}

export function useDeletePerson() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/persons/${id}`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.persons.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.persons.lists() });
    },
  });
}
